/**
 * Highcharts Initializer
 *
 * Initializes Highcharts from data attributes.
 * Works with both page-load content and AJAX-loaded content.
 */

(function () {
  "use strict";

  const HIGHCHARTS_BASE_URL = "https://code.highcharts.com";
  let highchartsLoadPromise = null;

  function getPendingChartContainers(container) {
    container = container || document;
    return container.querySelectorAll(
      ".js-highchart:not([data-chart-initialized])",
    );
  }

  function needsTreemapModule(charts) {
    return Array.from(charts).some((chart) => {
      const configJson = chart.dataset.chartConfig;
      if (!configJson) return false;

      try {
        const config = JSON.parse(configJson);
        if (config?.chart?.type === "treemap") return true;
        return (config?.series || []).some(
          (series) => series?.type === "treemap",
        );
      } catch (e) {
        return false;
      }
    });
  }

  function ensureScript(src) {
    const existingScript = document.querySelector(`script[src=\"${src}\"]`);

    if (existingScript) {
      if (existingScript.dataset.loaded === "true") {
        return Promise.resolve();
      }

      return new Promise((resolve, reject) => {
        existingScript.addEventListener("load", () => resolve(), {
          once: true,
        });
        existingScript.addEventListener(
          "error",
          () => reject(new Error(`Failed to load ${src}`)),
          {
            once: true,
          },
        );
      });
    }

    return new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = src;
      script.async = true;
      script.onload = function () {
        script.dataset.loaded = "true";
        resolve();
      };
      script.onerror = function () {
        reject(new Error(`Failed to load ${src}`));
      };
      document.head.appendChild(script);
    });
  }

  function hasExporting() {
    return (
      typeof Highcharts !== "undefined" &&
      typeof Highcharts.Chart?.prototype?.exportChart === "function"
    );
  }

  function hasExportData() {
    return (
      typeof Highcharts !== "undefined" &&
      typeof Highcharts.Chart?.prototype?.getCSV === "function"
    );
  }

  function ensureHighcharts(options = {}) {
    if (typeof Highcharts !== "undefined") {
      const tasks = [];
      if (options.needTreemap && !Highcharts.seriesTypes?.treemap) {
        tasks.push(ensureScript(`${HIGHCHARTS_BASE_URL}/modules/treemap.js`));
      }
      if (!hasExporting()) {
        tasks.push(ensureScript(`${HIGHCHARTS_BASE_URL}/modules/exporting.js`));
      }
      if (!hasExportData()) {
        tasks.push(
          ensureScript(`${HIGHCHARTS_BASE_URL}/modules/export-data.js`),
        );
      }
      return tasks.length
        ? Promise.all(tasks).then(() => undefined)
        : Promise.resolve();
    }

    if (!highchartsLoadPromise) {
      highchartsLoadPromise = ensureScript(
        `${HIGHCHARTS_BASE_URL}/highcharts.js`,
      )
        .then(() => ensureScript(`${HIGHCHARTS_BASE_URL}/highcharts-more.js`))
        .then(() => ensureScript(`${HIGHCHARTS_BASE_URL}/modules/exporting.js`))
        .then(() =>
          ensureScript(`${HIGHCHARTS_BASE_URL}/modules/export-data.js`),
        )
        .catch((error) => {
          highchartsLoadPromise = null;
          throw error;
        });
    }

    if (options.needTreemap) {
      return highchartsLoadPromise.then(() => {
        if (Highcharts.seriesTypes?.treemap) {
          return Promise.resolve();
        }
        return ensureScript(`${HIGHCHARTS_BASE_URL}/modules/treemap.js`);
      });
    }

    return highchartsLoadPromise;
  }

  /**
   * Initialize a single chart container
   */
  function initChart(container) {
    if (!container || container.dataset.chartInitialized) {
      return;
    }

    const configJson = container.dataset.chartConfig;
    if (!configJson) {
      console.warn("[Highcharts Init] No config found for:", container.id);
      return;
    }

    try {
      const config = JSON.parse(configJson);
      console.log(
        "[Highcharts Init] Initializing chart:",
        container.id,
        config,
      );

      if (typeof Highcharts !== "undefined") {
        // Ensure responsive chart by not setting fixed dimensions
        config.chart = config.chart || {};
        config.chart.height = config.chart.height || null;
        config.chart.width = null; // Use container width
        config.chart.reflow = true; // Enable auto-resize

        // Configure exporting (CSV download, fullscreen, data table) without
        // showing Highcharts' default context (hamburger) button. Our custom
        // action buttons in the chart header invoke these APIs directly.
        config.exporting = config.exporting || {};
        config.exporting.enabled = true;
        config.exporting.fallbackToExportServer = false;
        config.exporting.buttons = config.exporting.buttons || {};
        config.exporting.buttons.contextButton = Object.assign(
          {},
          config.exporting.buttons.contextButton,
          { enabled: false },
        );
        if (!config.exporting.filename) {
          // Prefer the human-readable chart title as filename. Highcharts
          // sanitizes it to a safe filename automatically.
          const titleText =
            container.dataset.chartTitle || config?.title?.text || "graafik";
          config.exporting.filename = String(titleText).trim() || "graafik";
        }

        Highcharts.chart(container.id, config);
        container.dataset.chartInitialized = "true";
        console.log("[Highcharts Init] Chart rendered:", container.id);
      } else {
        console.error("[Highcharts Init] Highcharts not available");
      }
    } catch (e) {
      console.error("[Highcharts Init] Error parsing config or rendering:", e);
    }
  }

  /**
   * Initialize all charts in a container (or document)
   */
  function initCharts(container) {
    const charts = getPendingChartContainers(container);

    if (!charts.length) {
      return;
    }

    console.log(
      "[Highcharts Init] Found",
      charts.length,
      "charts to initialize",
    );

    charts.forEach(initChart);
  }

  /**
   * Wait for Highcharts to be available, then init
   */
  function waitForHighchartsAndInit(container) {
    const charts = getPendingChartContainers(container);

    if (!charts.length) {
      return;
    }

    const needTreemap = needsTreemapModule(charts);

    ensureHighcharts({ needTreemap })
      .then(() => initCharts(container))
      .catch((error) => {
        console.error(
          "[Highcharts Init] Failed to load Highcharts libraries",
          error,
        );
      });
  }

  // Expose global function for AJAX content
  window.oskaInitCharts = function (container) {
    console.log("[Highcharts Init] oskaInitCharts called");
    // Destroy any chart instances whose container has been removed from the
    // DOM (e.g. previous proposal modal content was replaced).
    if (typeof Highcharts !== "undefined" && Highcharts.charts) {
      Highcharts.charts.forEach(function (chart) {
        if (
          chart &&
          chart.renderTo &&
          !document.body.contains(chart.renderTo)
        ) {
          try {
            chart.destroy();
          } catch (e) {
            /* noop */
          }
        }
      });
    }
    waitForHighchartsAndInit(container);
  };

  // Initialize on page load
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      waitForHighchartsAndInit();
    });
  } else {
    waitForHighchartsAndInit();
  }

  // Also init on window load (in case Highcharts loads late)
  window.addEventListener("load", function () {
    waitForHighchartsAndInit();
  });

  // Reflow all charts on window resize (debounced)
  let resizeTimer;
  window.addEventListener("resize", function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      if (typeof Highcharts !== "undefined" && Highcharts.charts) {
        Highcharts.charts.forEach(function (chart) {
          if (chart) {
            chart.reflow();
          }
        });
      }
    }, 100);
  });

  /**
   * Find the Highcharts chart instance whose container is the given DOM
   * element. Looking up by element (not id) ensures we ignore stale chart
   * instances whose `renderTo` element has been removed from the DOM (which
   * happens when the proposal modal is re-opened with new AJAX content).
   */
  function findChartByElement(el) {
    if (!el || typeof Highcharts === "undefined" || !Highcharts.charts) {
      return null;
    }
    return (
      Highcharts.charts.find((chart) => chart && chart.renderTo === el) || null
    );
  }

  /**
   * Toggle the data table for a chart. Renders Highcharts' getTable()
   * output into the table container next to the chart.
   */
  function toggleChartTable(chart, button) {
    const targetId = button.dataset.chartTarget;
    const tableEl = document.querySelector(`[data-chart-table="${targetId}"]`);
    if (!tableEl) return;

    const isShown = !tableEl.hasAttribute("hidden");

    if (isShown) {
      tableEl.innerHTML = "";
      tableEl.setAttribute("hidden", "");
      button.setAttribute("aria-pressed", "false");
      return;
    }

    let tableHtml = "";
    try {
      tableHtml = chart.getTable ? chart.getTable() : "";
    } catch (e) {
      console.error("[Highcharts Init] getTable() failed", e);
    }

    if (!tableHtml) {
      tableHtml =
        '<p class="oska-chart__table-empty">Andmetabel pole saadaval.</p>';
    }

    tableEl.innerHTML = tableHtml;
    tableEl.removeAttribute("hidden");
    button.setAttribute("aria-pressed", "true");
  }

  // Delegate chart action button clicks (table / CSV / fullscreen).
  document.addEventListener("click", function (event) {
    const button = event.target.closest("[data-chart-target]");
    if (!button) return;

    const targetId = button.dataset.chartTarget;
    const chartEl = document.getElementById(targetId);
    const chart = findChartByElement(chartEl);
    if (!chart) {
      console.warn(
        "[Highcharts Init] No chart found for action button:",
        targetId,
      );
      return;
    }

    if (button.classList.contains("js-chart-action-csv")) {
      event.preventDefault();
      if (typeof chart.downloadCSV === "function") {
        chart.downloadCSV();
      } else {
        console.warn("[Highcharts Init] export-data module not loaded");
      }
      return;
    }

    if (button.classList.contains("js-chart-action-fullscreen")) {
      event.preventDefault();
      if (chart.fullscreen && typeof chart.fullscreen.toggle === "function") {
        chart.fullscreen.toggle();
      } else {
        console.warn("[Highcharts Init] exporting module not loaded");
      }
      return;
    }

    if (button.classList.contains("js-chart-action-table")) {
      event.preventDefault();
      toggleChartTable(chart, button);
    }
  });
})();
